import { Fragment, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  DocumentPlusIcon,
} from '@heroicons/react/24/outline';
import { documentsApi, clientsApi } from '@services/api';
import { useDebounce } from '@hooks';
import { formatMoney, isQuoteExpired, formatDate } from '@utils';
import { EmptyState } from '@components/common';
import type {
  DocumentListItem,
  DocumentListParams,
  DocumentStatus,
  DocumentType,
} from '@types';

const STATUSES: DocumentStatus[] = [
  'DRAFT',
  'ISSUED',
  'ACCEPTED',
  'DECLINED',
  'CONVERTED',
  'VOID',
  'PAID',
];

function TypeBadge({ type }: { type: DocumentType }) {
  const isInvoice = type === 'invoice';
  return (
    <span
      data-testid="doc-type-badge"
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        isInvoice
          ? 'bg-indigo-100 text-indigo-800'
          : 'bg-teal-100 text-teal-800'
      }`}
    >
      {isInvoice ? 'Invoice' : 'Quote'}
    </span>
  );
}

const STATUS_STYLES: Record<DocumentStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ISSUED: 'bg-blue-100 text-blue-800',
  ACCEPTED: 'bg-green-100 text-green-800',
  DECLINED: 'bg-red-100 text-red-800',
  CONVERTED: 'bg-purple-100 text-purple-800',
  VOID: 'bg-gray-200 text-gray-600 line-through',
  PAID: 'bg-emerald-100 text-emerald-800',
};

function StatusBadges({ doc }: { doc: DocumentListItem }) {
  const expired = isQuoteExpired(doc);
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        data-testid="doc-status-badge"
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[doc.status]}`}
      >
        {doc.status}
      </span>
      {expired && (
        <span
          data-testid="doc-expired-badge"
          className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800"
        >
          EXPIRED
        </span>
      )}
    </span>
  );
}

/** Month bucket label for grouping. Drafts (no issue_date) group first. */
function monthLabel(issueDate: string | null): string {
  if (!issueDate) return 'No issue date · Drafts';
  return new Date(`${issueDate}T00:00:00Z`).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const selectClass =
  'h-[42px] rounded-[9px] border-[1.5px] border-[#d7dfe9] bg-white px-3 text-[13px] font-semibold text-gray-600 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

export default function Documents() {
  const [searchParams] = useSearchParams();
  // Seed filters from the URL once so Dashboard stat-card deep links
  // (e.g. /documents?type=invoice&status=ISSUED) apply on arrival.
  const [type, setType] = useState<'' | DocumentType>(
    (searchParams.get('type') as DocumentType) || ''
  );
  const [status, setStatus] = useState<'' | DocumentStatus>(
    (searchParams.get('status') as DocumentStatus) || ''
  );
  const [clientId, setClientId] = useState(searchParams.get('client_id') || '');
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') || '');
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search, 300);

  const params: DocumentListParams = useMemo(
    () => ({
      ...(type ? { type } : {}),
      ...(status ? { status } : {}),
      ...(clientId ? { client_id: clientId } : {}),
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
      page,
    }),
    [type, status, clientId, dateFrom, dateTo, debouncedSearch, page]
  );

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.list(),
  });

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['documents', params],
    queryFn: () => documentsApi.list(params),
    placeholderData: keepPreviousData,
  });

  const resetToFirstPage = () => setPage(1);
  const hasFilters = !!(
    type ||
    status ||
    clientId ||
    dateFrom ||
    dateTo ||
    debouncedSearch
  );
  const clearAll = () => {
    setType('');
    setStatus('');
    setClientId('');
    setDateFrom('');
    setDateTo('');
    setSearch('');
    resetToFirstPage();
  };

  // Group rows by issue-date month. Sort by issue_date first (drafts with no
  // date on top, then newest issue_date first) so month groups are contiguous
  // and chronological regardless of the server's row order — otherwise
  // freshly-imported docs (recent created_at, old issue_date) scramble the
  // grouping.
  const groups = useMemo(() => {
    const items = [...(data?.items ?? [])].sort((a, b) => {
      if (!a.issue_date && !b.issue_date) return 0;
      if (!a.issue_date) return -1;
      if (!b.issue_date) return 1;
      return b.issue_date.localeCompare(a.issue_date);
    });
    const out: Array<{ label: string; rows: DocumentListItem[] }> = [];
    for (const doc of items) {
      const label = monthLabel(doc.issue_date);
      const last = out[out.length - 1];
      if (last && last.label === label) last.rows.push(doc);
      else out.push({ label, rows: [doc] });
    }
    return out;
  }, [data]);

  return (
    <div data-testid="documents-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-[26px] font-medium text-gray-900">
          Documents
        </h1>
        <Link
          to="/documents/new"
          data-testid="new-document-btn"
          className="inline-flex h-[42px] items-center gap-2 rounded-[9px] bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700"
        >
          <PlusIcon className="h-4 w-4" /> New document
        </Link>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1 sm:max-w-[360px]">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3.5 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-gray-400" />
          <input
            data-testid="filter-search"
            type="search"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              resetToFirstPage();
            }}
            placeholder="Search doc no, client, event"
            aria-label="Search documents"
            className="h-[42px] w-full rounded-[9px] border-[1.5px] border-[#d7dfe9] pl-10 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <select
          data-testid="filter-type"
          aria-label="Filter by type"
          value={type}
          onChange={e => {
            setType(e.target.value as '' | DocumentType);
            resetToFirstPage();
          }}
          className={selectClass}
        >
          <option value="">All types</option>
          <option value="invoice">Invoice</option>
          <option value="quote">Quote</option>
        </select>
        <select
          data-testid="filter-status"
          aria-label="Filter by status"
          value={status}
          onChange={e => {
            setStatus(e.target.value as '' | DocumentStatus);
            resetToFirstPage();
          }}
          className={selectClass}
        >
          <option value="">All statuses</option>
          {STATUSES.map(s => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          data-testid="filter-client"
          aria-label="Filter by client"
          value={clientId}
          onChange={e => {
            setClientId(e.target.value);
            resetToFirstPage();
          }}
          className={selectClass}
        >
          <option value="">All clients</option>
          {clients?.map(c => (
            <option key={c.id} value={c.id}>
              {c.display_name}
            </option>
          ))}
        </select>
        <input
          data-testid="filter-date-from"
          type="date"
          value={dateFrom}
          onChange={e => {
            setDateFrom(e.target.value);
            resetToFirstPage();
          }}
          aria-label="Issued from"
          className={selectClass}
        />
        <input
          data-testid="filter-date-to"
          type="date"
          value={dateTo}
          onChange={e => {
            setDateTo(e.target.value);
            resetToFirstPage();
          }}
          aria-label="Issued to"
          className={selectClass}
        />
        {hasFilters && (
          <button
            type="button"
            onClick={clearAll}
            className="text-[13px] font-semibold text-gray-500 underline hover:text-gray-700"
          >
            Clear all
          </button>
        )}
      </div>

      {/* States */}
      {isLoading ? (
        <div
          data-testid="documents-loading"
          className="overflow-hidden rounded-2xl border border-hairline bg-white shadow-sm"
        >
          {[0, 1, 2, 3, 4].map(i => (
            <div
              key={i}
              className="flex items-center gap-4 border-b border-gray-100 px-5 py-4 last:border-0"
            >
              <div className="h-3 w-32 rounded bg-gray-100" />
              <div className="h-[18px] w-14 rounded-full bg-gray-100" />
              <div className="h-3 flex-1 rounded bg-gray-100" />
              <div className="h-3 w-20 rounded bg-gray-100" />
            </div>
          ))}
        </div>
      ) : isError || !data ? (
        <div data-testid="documents-error">
          <EmptyState
            title="Couldn't load documents"
            description="Something went wrong fetching the list. Please try again."
            action={{ label: 'Retry', onClick: () => refetch() }}
          />
        </div>
      ) : data.items.length === 0 ? (
        <div
          data-testid="documents-empty"
          className="rounded-2xl border border-hairline bg-white px-6 py-14 text-center shadow-sm"
        >
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50">
            <DocumentPlusIcon className="h-7 w-7 text-primary-600" />
          </div>
          <h3 className="font-serif text-[19px] font-medium text-gray-900">
            {hasFilters
              ? 'No documents match your filters'
              : 'No documents yet'}
          </h3>
          <p className="mx-auto mt-1.5 max-w-sm text-[13px] text-gray-500">
            {hasFilters
              ? 'Try clearing a filter to widen the search.'
              : 'Create your first invoice or quote to get started.'}
          </p>
          {hasFilters ? (
            <button
              type="button"
              onClick={clearAll}
              className="mt-4 inline-flex h-[42px] items-center rounded-[9px] border-[1.5px] border-[#d7dfe9] bg-white px-5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Clear filters
            </button>
          ) : (
            <Link
              to="/documents/new"
              className="mt-4 inline-flex h-[42px] items-center gap-2 rounded-[9px] bg-primary-600 px-5 text-sm font-semibold text-white hover:bg-primary-700"
            >
              <PlusIcon className="h-4 w-4" /> Create your first document
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Desktop table (grouped by month). The table body scrolls inside
              this container so the page chrome stays put; the header sticks. */}
          <div
            className="hidden max-h-[calc(100vh-20rem)] overflow-auto rounded-2xl border border-hairline bg-white shadow-sm sm:block"
            data-testid="documents-table-wrap"
          >
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-[#fbfcfe]">
                <tr className="text-left font-mono text-[11px] uppercase tracking-[0.08em] text-gray-400 [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-[#fbfcfe]">
                  <th className="px-[18px] py-3 font-semibold">Doc No</th>
                  <th className="px-[18px] py-3 font-semibold">Type</th>
                  <th className="px-[18px] py-3 font-semibold">Client</th>
                  <th className="px-[18px] py-3 font-semibold">Event</th>
                  <th className="px-[18px] py-3 font-semibold">Issue date</th>
                  <th className="px-[18px] py-3 text-right font-semibold">
                    Total
                  </th>
                  <th className="px-[18px] py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {groups.map(group => (
                  <Fragment key={group.label}>
                    <tr>
                      <td
                        colSpan={7}
                        className="border-y border-hairline bg-[#f1f5f9] px-[18px] py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-gray-500"
                      >
                        {group.label}
                      </td>
                    </tr>
                    {group.rows.map(doc => (
                      <tr
                        key={doc.id}
                        data-testid="document-row"
                        data-doc-id={doc.id}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="whitespace-nowrap px-[18px] py-3.5">
                          <Link
                            data-testid="document-link"
                            to={`/documents/${doc.id}`}
                            className="font-mono text-[13px] font-medium text-primary-600 hover:underline"
                          >
                            {doc.doc_no ?? (
                              <span className="font-sans italic text-gray-400">
                                Draft
                              </span>
                            )}
                          </Link>
                        </td>
                        <td className="px-[18px] py-3.5">
                          <TypeBadge type={doc.type} />
                        </td>
                        <td className="px-[18px] py-3.5 font-medium text-gray-900">
                          {doc.client_name}
                        </td>
                        <td className="px-[18px] py-3.5 text-gray-600">
                          {doc.event_name ?? '—'}
                        </td>
                        <td className="whitespace-nowrap px-[18px] py-3.5 text-gray-600">
                          {doc.issue_date ? formatDate(doc.issue_date) : '—'}
                        </td>
                        <td className="whitespace-nowrap px-[18px] py-3.5 text-right font-mono font-semibold text-gray-900">
                          {formatMoney(doc.total)}
                        </td>
                        <td className="px-[18px] py-3.5">
                          <StatusBadges doc={doc} />
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 sm:hidden">
            {groups.map(group => (
              <div key={group.label} className="space-y-3">
                <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-gray-500">
                  {group.label}
                </div>
                {group.rows.map(doc => (
                  <Link
                    key={doc.id}
                    to={`/documents/${doc.id}`}
                    data-testid="document-card"
                    className="block rounded-xl border border-hairline bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between">
                      <span className="font-mono text-[13px] font-medium text-primary-600">
                        {doc.doc_no ?? (
                          <span className="font-sans italic text-gray-400">
                            Draft
                          </span>
                        )}
                      </span>
                      <TypeBadge type={doc.type} />
                    </div>
                    <div className="mt-2.5 font-medium text-gray-900">
                      {doc.client_name}
                    </div>
                    <div className="text-[13px] text-gray-500">
                      {doc.event_name ?? '—'}
                    </div>
                    <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
                      <StatusBadges doc={doc} />
                      <span className="font-mono font-semibold text-gray-900">
                        {formatMoney(doc.total)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Pagination */}
      {data && data.pagination.total_pages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span
            data-testid="documents-pagination-info"
            className="font-mono text-[12px] text-gray-400"
          >
            Page {data.pagination.page} of {data.pagination.total_pages} ·{' '}
            {data.pagination.total_items} documents
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!data.pagination.has_prev || isFetching}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="h-9 rounded-lg border-[1.5px] border-gray-200 bg-white px-3.5 text-[13px] font-medium text-gray-600 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!data.pagination.has_next || isFetching}
              onClick={() => setPage(p => p + 1)}
              className="h-9 rounded-lg border-[1.5px] border-gray-200 bg-white px-3.5 text-[13px] font-medium text-gray-600 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
